import React, { useState } from 'react';
import { Sparkles, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { generateCoverPrompt, generateCoverImage, uploadLessonCover, clearPrompt } from '../../lib/api';

type Props = {
  coverUrl?: string;
  lessonId?: number;
  lessonTitle?: string;
  learningObjectives?: string;
  courseId?: number;
  onCoverGenerated?: (imageUrl: string) => void;
};

export const CoverImageUploader: React.FC<Props> = ({ 
  coverUrl, 
  lessonId,
  lessonTitle = '', 
  learningObjectives = '{}',
  courseId,
  onCoverGenerated 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [promptResult, setPromptResult] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [fromCache, setFromCache] = useState<boolean | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  const generateImagePrompt = async () => {
    if (!lessonId) return;
    
    setIsGenerating(true);
    setPromptResult(null);
    setFromCache(null);
    
    try {
      let objectivesText = '';
      try {
        const objectives = JSON.parse(learningObjectives);
        objectivesText = Object.entries(objectives)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
      } catch (e) {
        objectivesText = learningObjectives;
      }
      
      console.log('Generating cover prompt for lesson:', lessonId, lessonTitle);
      console.log('Learning objectives:', objectivesText);
      
      const response = await generateCoverPrompt(lessonId, lessonTitle, objectivesText, courseId);
      
      if (response.data.type === 'error') {
        const errorMessage = response.data.content || '生成提示词失败';
        alert(`错误：${errorMessage}`);
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + errorMessage);
        return;
      }
      
      const result = response.data.prompt;
      setFromCache(response.data.from_cache);
      
      console.log('Received prompt from AI:', result, '(from cache:', response.data.from_cache, ')');
      setPromptResult(result);
    } catch (error) {
      console.error('生成提示词失败:', error);
      if (error.response && error.response.data && error.response.data.type === 'error') {
        const errorMessage = error.response.data.content || '生成提示词失败';
        alert(`错误：${errorMessage}`);
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + errorMessage);
      } else {
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + (error instanceof Error ? error.message : String(error)));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearPrompt = async () => {
    if (!lessonId) return;
    
    if (!confirm('确定要清空现有提示词并重新生成吗？')) {
      return;
    }
    
    setIsClearing(true);
    try {
      await clearPrompt(lessonId, 'cover');
      setPromptResult(null);
      setFromCache(null);
      
      await generateImagePrompt();
    } catch (error) {
      console.error('清空提示词失败:', error);
      alert('清空提示词失败，请重试。');
    } finally {
      setIsClearing(false);
    }
  };

  const generateCoverImg = async () => {
    if (!promptResult || !lessonId) return;
    
    setIsGeneratingImage(true);
    
    try {
      console.log('Generating cover image with prompt:', promptResult);
      const response = await generateCoverImage(lessonId, promptResult);
      
      // 检查是否为type:error消息
      if (response.data.type === 'error') {
        const errorMessage = response.data.content || '生成封面图片失败';
        alert(`错误：${errorMessage}`);
        return;
      }
      
      const imageUrl = response.data.image_url;
      
      console.log('Received cover image URL:', imageUrl);
      
      // 调用uploadLessonCover方法，将图片URL保存到数据库
      console.log('Uploading cover image URL to database for lesson:', lessonId);
      const uploadResponse = await uploadLessonCover(lessonId, imageUrl);
      
      // 检查上传响应是否为type:error消息
      if (uploadResponse.data.type === 'error') {
        const errorMessage = uploadResponse.data.content || '上传封面图片失败';
        alert(`错误：${errorMessage}`);
        return;
      }
      
      console.log('Cover image URL uploaded successfully');
      
      // 调用回调函数更新父组件中的coverUrl
      if (onCoverGenerated) {
        onCoverGenerated(imageUrl);
      }
      
      // 关闭弹窗
      setPromptResult(null);
      
      // 刷新页面以显示新图片
      window.location.reload();
    } catch (error) {
      console.error('生成封面图片失败:', error);
      // 检查错误响应中是否包含type:error消息
      if (error.response && error.response.data && error.response.data.type === 'error') {
        const errorMessage = error.response.data.content || '生成封面图片失败';
        alert(`错误：${errorMessage}`);
      } else {
        alert('生成封面图片失败，请重试。\n\n错误信息：' + (error instanceof Error ? error.message : String(error)));
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  return (
    <div className="p-8 flex flex-col items-center justify-center space-y-6">
      {coverUrl ? (
        <div className="w-full max-w-md aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden border shadow-sm">
          <img 
            src={coverUrl} 
            alt="Cover" 
            className="w-full h-full object-contain" 
            onError={(e) => { (e.target as HTMLImageElement).src = "https://via.placeholder.com/800x600?text=Image+Load+Error"; }} 
          />
        </div>
      ) : (
        <div className="w-full max-w-md aspect-[4/3] bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400">
          <p>No cover image set.</p>
        </div>
      )}
      
      <button 
        onClick={generateImagePrompt}
        disabled={isGenerating}
        className="flex items-center gap-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-lg hover:bg-blue-200 transition-colors font-medium border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Sparkles size={18} />
        {isGenerating ? '生成中...' : '获取文生图提示词'}
      </button>
      
      {promptResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">文生图提示词</h3>
              {fromCache !== null && (
                <span className={`text-xs px-2 py-1 rounded-full ${fromCache ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {fromCache ? '来自缓存' : '新生成'}
                </span>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-gray-700 mb-4">
              {promptResult}
            </div>
            <div className="flex justify-end gap-3">
              <button 
                onClick={handleClearPrompt}
                disabled={isClearing}
                className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={isClearing ? 'animate-spin' : ''} />
                {isClearing ? '清空中...' : '重新生成'}
              </button>
              <button 
                onClick={generateCoverImg}
                disabled={isGeneratingImage}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImageIcon size={16} />
                {isGeneratingImage ? '生图中...' : '继续生图'}
              </button>
              <button 
                onClick={() => setPromptResult(null)}
                className="bg-gray-900 text-white px-5 py-2 rounded-lg hover:bg-gray-800 font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
